import type { CSSProperties } from 'preact'

export const uiTokens = {
  radius: {
    sm: '8px',
    md: '16px',
  },
  colors: {
    white: '#fff',
    gray50: '#fafafa',
    gray100: '#f5f5f5',
    gray125: '#f1f5f7',
    gray150: '#ebebeb',
    gray200: '#e5e5e5',
    gray400: '#999',
    gray500: '#666',
    gray900: '#1a1a1a',
    linkBlue: '#2563eb',
    buttonDark: '#2d3436',
    toggleTrackOff: '#607180',
    toggleTrackDisabled: '#dfe8ee',
  },
  zIndex: {
    max: '2147483647',
  },
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const

export const panelShadow = '0 4px 24px rgba(0, 0, 0, 0.15)'
export const modalPanelShadow = '0 8px 32px rgba(0, 0, 0, 0.2)'

export const titleStyle: CSSProperties = {
  fontSize: '1.25em',
  fontWeight: '700',
  color: uiTokens.colors.gray900,
}

export const titleWithMarginStyle: CSSProperties = {
  ...titleStyle,
  marginBottom: '0.75rem',
}

export const subtitleStyle: CSSProperties = {
  fontSize: '1em',
  fontWeight: '700',
  color: uiTokens.colors.gray900,
}

export const bodyTextStyle: CSSProperties = {
  fontSize: '0.9em',
  fontWeight: '400',
  color: uiTokens.colors.gray500,
  lineHeight: '1.5',
}

export const captionStyle: CSSProperties = {
  fontSize: '0.85em',
  fontWeight: '400',
  color: uiTokens.colors.gray500,
  lineHeight: '1.5',
}

export const paragraphStyle: CSSProperties = {
  display: 'block',
  marginBottom: '1rem',
  ...bodyTextStyle,
}

export const linkStyle: CSSProperties = {
  color: uiTokens.colors.gray900,
  fontWeight: '600',
  textDecoration: 'none',
}

export const policyLinkStyle: CSSProperties = {
  fontSize: '0.85em',
  color: uiTokens.colors.gray500,
  textDecoration: 'none',
}

export const privacyLinkStyle: CSSProperties = {
  fontSize: '0.85em',
  color: uiTokens.colors.linkBlue,
  textDecoration: 'underline',
  display: 'block',
  marginBottom: '0.5rem',
  width: 'fit-content',
}

export const dialogHeaderPaddingStyle: CSSProperties = {
  padding: '1.25rem 1.5rem 1rem',
}

export const modalHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 24px',
  borderBottom: `1px solid ${uiTokens.colors.gray200}`,
}

export const scrollableBodyStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}

export const floatingPanelBaseStyle: CSSProperties = {
  position: 'fixed',
  bottom: '1rem',
  left: '1rem',
  background: uiTokens.colors.white,
  borderRadius: uiTokens.radius.md,
  boxShadow: panelShadow,
  maxHeight: '80vh',
  width: 'calc(100vw - 2rem)',
  zIndex: uiTokens.zIndex.max,
  overflow: 'hidden',
  transform: 'translateY(1rem)',
  transition: 'opacity 0.25s ease, visibility 0.25s ease, transform 0.25s ease',
  fontFamily: uiTokens.fontFamily,
  fontSize: '16px',
  lineHeight: '1.5',
  display: 'flex',
  flexDirection: 'column',
}

export const modalBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  zIndex: uiTokens.zIndex.max,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  fontFamily: uiTokens.fontFamily,
  fontSize: '16px',
  lineHeight: '1.5',
}

export const modalBackdropMobileStyle: CSSProperties = {
  padding: 0,
  alignItems: 'stretch',
  justifyContent: 'stretch',
}

export const modalPanelStyle: CSSProperties = {
  background: uiTokens.colors.white,
  borderRadius: uiTokens.radius.md,
  boxShadow: modalPanelShadow,
  width: '100%',
  maxWidth: '720px',
  height: 'min(90vh, 640px)',
  maxHeight: 'min(90vh, 720px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

export const modalPanelMobileStyle: CSSProperties = {
  maxWidth: 'none',
  maxHeight: 'none',
  height: '100%',
  borderRadius: 0,
  boxShadow: 'none',
}

export const actionBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
}

export const actionBarLeftStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
}

export const actionBarMobileStyle: CSSProperties = {
  flexDirection: 'column',
  alignItems: 'stretch',
}

export const actionBarLeftMobileStyle: CSSProperties = {
  flexDirection: 'column',
  width: '100%',
}

export const buttonFullWidthStyle: CSSProperties = {
  width: '100%',
}

export const verticalActionStackStyle: CSSProperties = {
  padding: '1rem 1.5rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}

export const policyFooterStyle: CSSProperties = {
  padding: '0.25rem 1.5rem',
  background: uiTokens.colors.gray100,
  borderTop: `1px solid ${uiTokens.colors.gray200}`,
}
